// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-depth */
// eslint-enable no-implicit-coercion
'use client';
import { useCallback, useEffect, useState, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ChatCompletionStreamResponse, ChatMessageContent, ToolCall, ToolCallFunction } from '~/common/types';
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert';
import { API_BASE_URL } from '~/config';
import { LineDecoder, SSEDecoder } from '~/lib/chat';
import { Model } from '~/services/models.types';
import { EmptyLLMOutput } from '../empty-llm-state';
import { LLMInput } from '../llm-input';
import { useRequestState } from '../request.context';
import ChatList from './chat-list';

export interface ChatInterfaceProps {
  readonly apiKey: string;
  readonly model: Model<'text'>;
}

const controller = new AbortController();

export function ChatInterface({ apiKey, model }: ChatInterfaceProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [requestStatus, setRequestStatus] = useState<string | null>(null);
  const { chatState, setChatState, setSelectedTab } = useRequestState();

  const ref = useRef<HTMLDivElement | null>(null);

  function handleScroll() {
    ref?.current?.scrollIntoView({ block: 'start' });
  }

  useEffect(() => {
    setSelectedTab('chat');
  }, []);

  // Reset the error message when the user resets a conversation
  useEffect(() => {
    if (chatState.messages.length === 0) {
      setRequestStatus(null);
    }
  }, [chatState.messages]);

  // eslint-disable-next-line complexity
  const fetchChatCompletion = async (text: string, base64Images?: string[], asFunctionResponse?: boolean) => {
    if (!apiKey) {
      return;
    }

    let updatedMessages = [...chatState.messages];

    try {
      setRequestStatus(null);
      setIsLoading(true);
      const startTime = Date.now();
      let firstTokenReceivedTime: number | undefined;
      let totalTokensReceived = 0;

      // If the model is alpaca, we need to reset the used flag
      // this is a workaround for the alpaca model not supporting multi-turn chat
      if (model.conversationConfig?.style === 'alpaca') {
        updatedMessages = updatedMessages.map((m) => ({
          ...m,
          metadata: {
            // eslint-disable-next-line unicorn/no-useless-fallback-in-spread
            ...(m.metadata || {}),
            used: false,
          },
        }));
      }

      let content: string | ChatMessageContent[] = text;
      if (model.tags.includes('Image') && (base64Images?.length ?? 0) > 0) {
        // There is a bug in the backend that we always want to pretend to be jpeg
        // so I need to replace the prefix from the HTML standard
        // data:image/png;base64 or whatever image type, into jpeg
        const base64Image = base64Images && base64Images.length > 0 ? base64Images[0] ?? '' : '';

        if (!base64Image) {
          // eslint-disable-next-line no-alert
          alert('Your browser failed to upload the image, please try using anther browser.');
          return;
        }

        // Ensure replace memetype with image/jpeg in the base64 string
        const jpegBase64Image = base64Image.replace(/^data:image\/[a-z]+;base64,/u, 'data:image/jpeg;base64,');
        content = [
          {
            type: 'text',
            text,
          },
          {
            type: 'image_url',
            // eslint-disable-next-line camelcase
            image_url: {
              url: jpegBase64Image,
            },
          },
        ];
      }
      const newMessage = {
        id: uuidv4(),
        content,
        role: asFunctionResponse ? 'tool' : 'user',
        name: asFunctionResponse
          ? updatedMessages[updatedMessages.length - 1]?.tool_calls?.[0]?.function?.name
          : undefined,
      };
      updatedMessages.push(newMessage as any, {
        content: '',
        id: uuidv4(),
        role: 'assistant' as const,
        metadata: {
          totalTokens: 0,
          firstTokenTime: 0,
          averageTokenTime: 0,
          perplexity: null,
        },
      });
      setChatState({ field: 'messages', value: [...updatedMessages] });

      handleScroll();

      const streaming = true;

      // TODO: Wrap this into a Node client library similar to https://github.com/openai/openai-node
      const response = await fetch(`${API_BASE_URL}/inference/v1/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          Authorization: `Bearer ${apiKey}`,
        },
        signal: controller.signal,

        body: JSON.stringify({
          ...chatState,
          model: model.name,
          stream: streaming,
          n: 1,
          logprobs: 1,
          messages:
            model.conversationConfig?.style === 'alpaca'
              ? [
                  {
                    role: 'user',
                    content,
                  },
                ]
              : [...chatState.messages, newMessage].map((m) => ({
                  role: m.role,
                  content: m.content,
                })),
        }),
      });

      if (response.status === 429) {
        setRequestStatus('over_quota');
        setIsLoading(false);
        updatedMessages.pop();
        setChatState({ field: 'messages', value: [...updatedMessages] });
        return;
      }
      if (response.status !== 200) {
        const errorFromServer = await response.json().catch(() => null);
        const errorMessage = errorFromServer.error?.message;
        if (errorMessage === undefined) {
          setRequestStatus('unknown_error');
        } else {
          setRequestStatus(`${String(errorMessage)} To start a new conversation, please refresh the page`);
        }
        setIsLoading(false);
        updatedMessages.pop();
        setChatState({ field: 'messages', value: [...updatedMessages] });

        return;
      }
      if (!response.body) {
        setRequestStatus('unknown_error');
        setIsLoading(false);
        updatedMessages.pop();
        setChatState({ field: 'messages', value: [...updatedMessages] });

        return;
      }
      handleScroll();

      // Support for non-streaming responses. Not always in use, this is needed sometimes to get an initial model
      // push out the door when FC or some other feature that needs to be parsed is not yet fully supported
      if (!streaming) {
        const json = await response.json();

        const totalTime = Date.now() - startTime;
        const averageTime = totalTime / (json.usage.total_tokens - 1);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        updatedMessages[updatedMessages.length - 1]!.content = json.choices[0].message.content;
        updatedMessages[updatedMessages.length - 1]!.metadata.totalTokens = json.usage.total_tokens; // eslint-disable-line @typescript-eslint/no-non-null-assertion
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        updatedMessages[updatedMessages.length - 1]!.metadata.averageTokenTime = 1000 / averageTime;

        setChatState({ field: 'messages', value: [...updatedMessages] });

        return;
      }

      // Read the response as a stream of data
      const reader = response.body.getReader();
      const toolCallFunction: ToolCallFunction = {};

      const strDecoder = new TextDecoder('utf8');
      const decoder = new SSEDecoder();
      const lineDecoder = new LineDecoder();

      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        // Massage and parse the chunk of data
        const lines = lineDecoder.decode(strDecoder.decode(value));

        // Clean up the data
        for (const line of lines) {
          const decodedSSE = decoder.decode(line);
          if (decodedSSE === null) {
            continue;
          }
          if (decodedSSE.data?.startsWith('{')) {
            const data: ChatCompletionStreamResponse = JSON.parse(decodedSSE.data);

            const toolCalls: ToolCall[] | undefined = data.choices[0]?.delta.tool_calls;

            if (toolCalls) {
              for (const call of toolCalls) {
                for (const [key, val] of Object.entries(call.function)) {
                  if (toolCallFunction[key]) {
                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    toolCallFunction[key] += val;
                  } else {
                    toolCallFunction[key] = val;
                  }
                }
              }
            }

            if (Object.keys(toolCallFunction).length > 0) {
              if (!updatedMessages[updatedMessages.length - 1]?.tool_calls) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                updatedMessages[updatedMessages.length - 1]!.tool_calls = [];
              }
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              updatedMessages[updatedMessages.length - 1]!.content = JSON.stringify(toolCallFunction, null, 2);
            } else {
              // eslint-disable-next-line @typescript-eslint/restrict-plus-operands, @typescript-eslint/no-non-null-assertion
              updatedMessages[updatedMessages.length - 1]!.content += data.choices[0]?.delta.content ?? '';
            }

            const tokensReceived = data.choices[0]?.logprobs?.tokens.length ?? 0;
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            updatedMessages[updatedMessages.length - 1]!.metadata.perplexity = data.choices[0]?.logprobs?.tokens as any;
            if (firstTokenReceivedTime === undefined) {
              firstTokenReceivedTime = Date.now();
              updatedMessages[updatedMessages.length - 1]!.metadata.firstTokenTime = firstTokenReceivedTime - startTime; // eslint-disable-line @typescript-eslint/no-non-null-assertion
            }
            totalTokensReceived += tokensReceived;
            if (totalTokensReceived === 0) {
              // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
              totalTokensReceived += data.usage?.completion_tokens ?? 0;
            }

            if (firstTokenReceivedTime !== undefined) {
              const totalTime = Date.now() - firstTokenReceivedTime;
              const averageTime = totalTime / (totalTokensReceived - 1);
              updatedMessages[updatedMessages.length - 1]!.metadata.totalTokens = totalTokensReceived; // eslint-disable-line @typescript-eslint/no-non-null-assertion
              updatedMessages[updatedMessages.length - 1]!.metadata.averageTokenTime = 1000 / averageTime; // eslint-disable-line @typescript-eslint/no-non-null-assertion
            }
            setChatState({ field: 'messages', value: [...updatedMessages] });
          }
        }
      }
    } catch (e: any) {
      if (e?.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.error(e);
        setRequestStatus('unknown_error');
        updatedMessages.pop();
        setChatState({ field: 'messages', value: [...updatedMessages] });
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (updatedMessages[updatedMessages.length - 1]?.tool_calls) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      updatedMessages[updatedMessages.length - 1]!.tool_calls = [
        {
          id: uuidv4(),
          index: 0,
          type: 'function',
          function: JSON.parse(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            updatedMessages[updatedMessages.length - 1]!.content as string,
          ),
        },
      ];
    }
    setIsLoading(false);
  };

  const returnToolCallResponse = useCallback(
    (response: string) => {
      fetchChatCompletion(response, undefined, true);
    },
    [fetchChatCompletion],
  );

  return (
    <>
      {model.conversationConfig?.style === 'alpaca' && (
        <Alert color="warning" className="mr-4 mt-4">
          <AlertTitle>This model does not support multi-turn chat</AlertTitle>
          <AlertDescription>
            Please note that this model is trained using a single turn chat dataset, and will not work well with
            multi-turn chat. As such only the most recent message will be used.
          </AlertDescription>
        </Alert>
      )}
      {chatState.messages.length === 0 && (
        <div className="mx-auto mt-8 md:pt-16">
          <EmptyLLMOutput model={model} apiMode={'chat'} />
        </div>
      )}
      <ChatList
        innerRef={ref}
        messages={chatState.messages}
        isLoading={isLoading}
        returnToolCallResponse={returnToolCallResponse}
        markdown={model.tags.includes('Markdown')}
      >
        {requestStatus ? (
          <Alert color="error" className="mr-4">
            <AlertTitle>Error Generating response</AlertTitle>
            <AlertDescription>
              {requestStatus === 'over_quota'
                ? 'You have exceeded your max requests per hour, please try again later.'
                : requestStatus === 'unknown_error'
                  ? 'There was an error generating a response, please try again later.'
                  : `API Error: ${requestStatus}`}
            </AlertDescription>
          </Alert>
        ) : null}
      </ChatList>
      <LLMInput
        disabled={
          chatState.messages.length > 0 &&
          (chatState.messages[chatState.messages.length - 1]?.tool_calls?.length || 0) > 0
        }
        onSubmit={fetchChatCompletion}
        multiModal={model.tags.includes('Image')}
        isLoading={isLoading}
        onAbordRequest={() => {
          controller.abort();
          setIsLoading(false);
        }}
      />
    </>
  );
}
